import _ from "lodash";
import { ADD_ON_TYPES } from "../../../modules/AddOnManagerConstants";

export const EMPTY_OBJECT = {};

export const SOURCE = {
  QUERY: "query",
  MANUAL: "manual",
  PLAN_CHANGE: "plan_change",
};

const _getIsCheckedStatus = ({
  addOnsStatus,
  configurationState,
  addOnId,
  entityId,
}) => {
  const addOnStatus = _.get(addOnsStatus, addOnId, EMPTY_OBJECT);
  const addOnType = _.get(addOnStatus, "addOnType");
  const configState = _.get(configurationState, addOnId, EMPTY_OBJECT);
  const addedEntities = _.get(configState, "addedEntities", []);
  const removedEntities = _.get(configState, "removedEntities", []);

  const curriculumStatus = _.get(addOnStatus, "curriculumStatus", null);
  const curriculum = _.find(
    curriculumStatus,
    program => program.id == entityId // For some addOns curriculumId is number
  );

  let source = _.get(addOnStatus, "source");
  let isPromoted = _.get(addOnStatus, "isPromoted");
  let isDemoted = _.get(addOnStatus, "isDemoted");

  if (addOnType === ADD_ON_TYPES.CURRICULUM_LEVEL) {
    source = _.get(curriculum, "source");
    isPromoted = _.get(curriculum, "isPromoted");
    isDemoted = _.get(curriculum, "isDemoted");
  }

  const isCheckedFromQuery = _.isEmpty(curriculumStatus)
    ? _.get(addOnStatus, "checked", false)
    : _.get(curriculum, "checked", false);

  let isChecked = isCheckedFromQuery;

  if (_.includes(addedEntities, entityId)) {
    isChecked = true;
  } else if (_.includes(removedEntities, entityId)) {
    isChecked = false;
  }

  // Extract isDisabled from curriculum status
  const isDisabled = _.get(curriculum, "isDisabled", false);

  return {
    isChecked,
    source:
      isCheckedFromQuery && source !== SOURCE.PLAN_CHANGE
        ? SOURCE.QUERY
        : SOURCE.MANUAL,
    isPromoted,
    isDemoted,
    isDisabled,
  };
};

const _getAddOnId = headerId => {
  return _.replace(headerId, /_(gearicon|checkbox)/, "");
};

export const getDatagridHeaders = ({ addOnOptions }) => {
  const headers = [
    {
      id: "name",
      columnKey: "name",
      width: 220,
    },
    ..._.flatMap(addOnOptions, addOn => [
      {
        id: `${addOn.id}_checkbox`,
        width: 110,
      },
      {
        id: `${addOn.id}_gearicon`,
        width: 110,
      },
    ]),
  ];

  return headers;
};

const _getGearIconState = ({
  isChecked,
  isConfigurable,
  needsConfiguration,
  source,
}) => {
  const hasConfigurationError =
    isChecked && needsConfiguration && _.isEqual(source, SOURCE.MANUAL);

  if (hasConfigurationError) {
    return {
      hasError: true,
      isDisabled: false,
    };
  }

  return {
    hasError: false,
    isDisabled: !isConfigurable,
  };
};

const _getGearIconCellData = ({
  isConfigurable,
  isChecked,
  needsConfiguration,
  addOnId,
  entityId,
  toggleAddOn,
  source,
}) => {
  const { hasError, isDisabled } = _getGearIconState({
    isConfigurable,
    isChecked,
    needsConfiguration,
    source,
  });

  return {
    value: isConfigurable ? "SettingsOutlined" : "",
    cellType: "icon",
    isDisabled,
    tooltip: hasError ? "You need to configure this add-on" : "",
    hasError,
    onIconClick: () => toggleAddOn({ addOnId, entityId }),
  };
};

const _getCellData = ({
  id,
  entity,
  isAccountDatagrid,
  addOnsStatus,
  toggleAddOn,
  configurationState,
}) => {
  const entityId = _.get(entity, "id", null);
  const name = isAccountDatagrid
    ? _.get(entity, "name", "")
    : _.get(entity, "label", "");

  if (_.isEqual(id, "name")) {
    return {
      value: name,
      cellType: "text",
      isEditable: false,
    };
  }

  const addOnId = _getAddOnId(id);

  const isConfigurable = _.get(
    addOnsStatus,
    [addOnId, "isConfigurable"],
    false
  );

  const {
    isChecked,
    source,
    isDemoted,
    isPromoted,
    isDisabled,
  } = _getIsCheckedStatus({
    addOnsStatus,
    addOnId,
    entityId,
    configurationState,
  });

  const configStateForAddOn = _.get(configurationState, addOnId, {});
  const needsConfiguration = _.get(
    configStateForAddOn,
    "needsConfiguration",
    false
  );
  const disableCheckbox =
    (isChecked && needsConfiguration && _.isEqual(source, SOURCE.QUERY)) ||
    isDisabled;

  if (_.endsWith(id, "_checkbox")) {
    const checkboxValue = isChecked ? "checked" : "unchecked";

    const isTooltipVisibile = isPromoted || isDemoted;
    const tooltip = isPromoted
      ? "Now available in the new plan"
      : "Not available in the new plan";

    return {
      cellType: "checkbox",
      value: checkboxValue,
      isEditable: true,
      isDisabled: disableCheckbox,
      tooltip: isTooltipVisibile ? tooltip : null,
      isOverriden: {
        value: isDemoted || isPromoted ? true : false,
      },
    };
  }

  if (_.endsWith(id, "_gearicon")) {
    return _getGearIconCellData({
      isConfigurable: isDisabled ? false : isConfigurable,
      isChecked,
      needsConfiguration,
      addOnId,
      entityId,
      toggleAddOn,
      source,
    });
  }
};

export const getDatagridData = ({
  headers,
  entityList,
  isAccountDatagrid,
  addOnsStatus,
  toggleAddOn,
  configurationState,
}) => {
  const rowData = _.map(entityList, entity =>
    _.reduce(
      headers,
      (acc, header) => {
        const { id } = header;
        const cellData = _getCellData({
          id,
          entity,
          isAccountDatagrid,
          addOnsStatus,
          toggleAddOn,
          configurationState,
        });
        acc.columns.push(cellData);
        return acc;
      },
      { rowId: entity.id, columns: [] }
    )
  );
  return rowData;
};
